import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Building2, Mail } from 'lucide-react';
import { Alert, Button, PageHeader, TextField } from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import { useGstAccount, useGstActions } from '@/hooks/useGst';
import type { GstinLookup } from '@/types/gst';
import { GST_PATH, gstinFormatError, normalizeGstin } from '@/utils/gst';

const COLUMN = 'tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-5 md:tw-gap-6';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Check = { state: 'idle' } | { state: 'checking' } | { state: 'done'; result: GstinLookup } | { state: 'error'; message: string };

const LOOKUP_ERRORS = {
  cancelled: {
    field: 'This GSTIN is cancelled on the GST portal',
    body: 'GST records show it as cancelled. Check the number, or continue and get a regular invoice instead.',
  },
  not_found: {
    field: 'We couldn’t find this GSTIN in GST records',
    body: 'GST records don’t have this number. Check the number, or continue and get a regular invoice instead.',
  },
};

/** G1 / G2 / G3 Add GST details: GSTIN checked against GST records, invoice email, save. */
const GstDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { account, loading } = useGstAccount();
  const { lookup, save } = useGstActions();
  const input = useRef<HTMLInputElement>(null);

  const [gstin, setGstin] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [check, setCheck] = useState<Check>({ state: 'idle' });
  const [saving, setSaving] = useState(false);

  // Start from the saved details once they load.
  useEffect(() => {
    if (!account) return;
    setGstin(account.profile?.gstin ?? '');
    setEmail(account.profile?.email ?? user?.email ?? '');
  }, [account, user?.email]);

  const formatError = gstinFormatError(gstin);
  // Look the number up as soon as it's a well-formed GSTIN.
  useEffect(() => {
    if (!account || formatError) return setCheck({ state: 'idle' });
    let cancelled = false;
    setCheck({ state: 'checking' });
    lookup(gstin)
      .then((result) => !cancelled && setCheck({ state: 'done', result }))
      .catch((err: Error) => !cancelled && setCheck({ state: 'error', message: err.message }));
    return () => {
      cancelled = true;
    };
  }, [account, gstin, formatError, lookup]);

  const back = { to: GST_PATH, label: 'GST invoices' };
  const header = (
    <PageHeader
      title={account?.profile ? 'Edit GST details' : 'Add GST details'}
      subtitle="Get a tax invoice with your GSTIN to claim input tax credit on your plans."
      back={back}
    />
  );

  if (loading) {
    return (
      <div className={COLUMN}>
        {header}
        <div aria-busy className="tw-h-80 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
      </div>
    );
  }
  if (!account) {
    return (
      <div className={COLUMN}>
        {header}
        <Alert title="GST details are coming soon">Soon you’ll be able to add your GSTIN here and get tax invoices for your plans.</Alert>
        <Button variant="secondary" className="tw-self-start" onClick={() => navigate(GST_PATH)}>
          Back
        </Button>
      </div>
    );
  }

  const result = check.state === 'done' ? check.result : undefined;
  const verified = result?.status === 'active' ? result : undefined;
  const rejected = result && result.status !== 'active' ? LOOKUP_ERRORS[result.status] : undefined;
  const fieldError =
    (touched || gstin.length === 15) && formatError ? formatError : rejected?.field ?? (check.state === 'error' ? check.message : undefined);
  const emailError = email && !EMAIL.test(email) ? 'Enter a valid email address' : undefined;
  const sameState = verified && verified.stateName === account.sellerStateName;

  const submit = async () => {
    if (!verified || !email || emailError) return;
    setSaving(true);
    try {
      await save({ gstin, legalName: verified.legalName, billingAddress: verified.billingAddress, stateName: verified.stateName, email });
      toast.success('GST details saved');
      navigate(GST_PATH);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={COLUMN}>
      {header}

      <section aria-label="GST details" className="tw-flex tw-flex-col tw-gap-4 tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
        <TextField
          ref={input}
          label="GSTIN"
          leadingIcon={Building2}
          value={gstin}
          onChange={(e) => setGstin(normalizeGstin(e.target.value))}
          onBlur={() => setTouched(true)}
          placeholder="27ABCDE1234F1Z5"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={15}
          error={fieldError}
          helperText={check.state === 'checking' ? 'Checking GST records…' : '15-character GST number'}
        />
        {verified && (
          <Alert tone="success" title="GSTIN verified">
            {verified.legalName} · {verified.stateName} · Active
          </Alert>
        )}
        <TextField
          label="Legal business name"
          readOnly
          filled
          value={verified?.legalName ?? ''}
          placeholder="Filled from GST records"
          helperText={verified ? 'Filled from GST records' : undefined}
        />
        <TextField label="Billing address" readOnly filled value={verified?.billingAddress ?? ''} placeholder="Filled from GST records" />
        <TextField
          label="Email for invoice"
          type="email"
          leadingIcon={Mail}
          value={email}
          onChange={(e) => setEmail(e.target.value.trim())}
          autoComplete="email"
          error={emailError}
        />
      </section>

      {rejected ? (
        <>
          <Alert tone="danger" title="We can’t issue an invoice to this GSTIN">
            {rejected.body}
          </Alert>
          <div className="tw-flex tw-flex-col tw-gap-2.5 sm:tw-flex-row">
            <Button
              className="sm:tw-flex-1"
              onClick={() => {
                setGstin('');
                setTouched(false);
                input.current?.focus();
              }}
            >
              Try another GSTIN
            </Button>
            <Button variant="secondary" className="sm:tw-flex-1" onClick={() => navigate(GST_PATH)}>
              Continue without GST
            </Button>
          </div>
        </>
      ) : (
        <>
          {verified && (
            <Alert>
              {sameState
                ? `Your GSTIN and Protega are both registered in ${verified.stateName}, so the invoice shows CGST + SGST.`
                : `Your GSTIN is registered in ${verified.stateName} and Protega in ${account.sellerStateName}, so the invoice shows IGST.`}
            </Alert>
          )}
          <Button fullWidth onClick={submit} loading={saving} disabled={!verified || !email || Boolean(emailError)}>
            Save GST details
          </Button>
          <Button variant="link" className="tw-self-center" onClick={() => navigate(GST_PATH)}>
            Cancel
          </Button>
        </>
      )}
    </div>
  );
};

export default GstDetailsPage;
